import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CartItem } from "../../types";
import { useCart } from "../../hooks/use-cart";
import { formatPrice } from "../../helpers";
import cartBg from "../../assets/images/shopping-cart-bg.png";
import TotalPrice from "./TotalPrice";
import ShoppingCartHeader from "./ShoppingCartHeader";
import coverImage from "../../assets/images/cover-image.png";
import trashBg from "../../assets/images/trash-bg.png";
import cartBtnBg from "../../assets/images/cart-btn-bg.png";
import { FaTrash } from "react-icons/fa";
import { useAudioEffects } from "../../../../hooks/use-audio-effects";
import trashSound from "../../../../assets/audio/trash.wav";

/**
 * Minimal props only — uklonjene sve neiskorištene opcije.
 */
interface ShoppingCartProps {
  isOpen: boolean;
  items: CartItem[];
  // Optional small customizations
  title?: string;
  subtitle?: string;
  className?: string;
  currency?: string;
  onClose?: () => void;
  onPurchaseByCard?: () => void;
  onPurchaseByCash?: () => void;
  onItemClick?: (item: CartItem) => void;
  cardButtonText?: string;
  cashButtonText?: string;
  width?: string; // tailwind width class, npr "w-96"
  height?: string; // tailwind height class, npr "max-h-screen"
  showQuantity?: boolean;
  showSubtotal?: boolean;
  removeButtonAriaLabel?: string;
  emptyMessage?: string;
  showEmptyState?: boolean;
  RemoveIcon?: React.ElementType; // react-icons or custom component
}

/* ==========================
   Animation variants
   ========================== */
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 16, rotate: -1 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    rotate: 0,
    transition: { type: "spring", stiffness: 500, damping: 35 },
  },
  exit: { opacity: 0, scale: 0.98, y: 12, transition: { duration: 0.18 } },
};

/* ==========================
   Component
   ========================== */
const ShoppingCart: React.FC<ShoppingCartProps> = ({
  isOpen,
  items,
  title = "TUNING SHOPPING CART",
  subtitle = "Hello to everyone",
  className = "",
  currency = "USD",
  onClose = () => {},
  onPurchaseByCard = () => {},
  onPurchaseByCash = () => {},
  onItemClick = () => {},
  cardButtonText = "Purchase by Card",
  cashButtonText = "Purchase by Cash",
  width = "w-96",
  height = "max-h-screen",
  showQuantity = false,
  showSubtotal = true,
  removeButtonAriaLabel = "Remove item",
  emptyMessage = "Your cart is empty",
  showEmptyState = true,
  RemoveIcon = FaTrash,
}) => {
  // Cart helper from hook (assumes hook exists and returns { total, removeFromCart })
  const { total, removeFromCart } = useCart();
  const { playEffect } = useAudioEffects();

  /* --------------------------
     Accessibility: close on ESC
     -------------------------- */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  /* --------------------------
     Small helpers
     -------------------------- */
  const Remove = RemoveIcon as React.ElementType | null;

  const renderPrice = (price: number) =>
    formatPrice({ price, currency, locale: "en-US" });

  /* ==========================
     Render
     ========================== */
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={backdropVariants}
          aria-modal="true"
          role="dialog"
        >
          {/* Overlay (click outside to close) */}
          <motion.div
            className="absolute inset-0 bg-black/60"
            onClick={onClose}
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          />

          {/* Modal / panel */}
          <motion.div
            className={`relative ${width} ${height} rounded-lg overflow-hidden shadow-2xl p-8 ${className} flex flex-col justify-between z-10`}
            role="document"
            variants={modalVariants as any}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{ transformOrigin: "center" }}
          >
            {/* Background artwork */}
            <img
              src={cartBg}
              alt=""
              className="absolute top-0 left-0 w-full h-full z-[-1] object-cover"
              aria-hidden
            />

            {/* ----- Header ----- */}
            <ShoppingCartHeader
              className="border-[1px] border-white/15"
              titleWords={title
                .split(" ")
                .map((word, i) => ({ word, isHighlighted: i > 0 }))}
              description={subtitle}
              onClose={onClose}
            />

            {/* ----- Items list (scrollable) ----- */}
            <div className="flex-1 overflow-y-auto space-y-3 my-5 scrollbar-hide">
              {items.length === 0 && showEmptyState ? (
                <div className="text-center py-8 text-gray-400">
                  {emptyMessage}
                </div>
              ) : (
                items.map((item: CartItem) => (
                  <div
                    key={item.productId}
                    className="rounded-md p-3 bg-gradient-to-r from-[#272d39]/20 via-[#272d39]/50 to-[#272d39]/20"
                  >
                    <div className="flex items-center justify-between rounded-md from-[#3e4450] to-[#3e4450]/5 bg-gradient-to-r">
                      {/* Image / preview */}
                      <div
                        className="relative w-[84px] h-[84px] p-1 z-[1] rounded-md cursor-pointer"
                        onClick={() => onItemClick(item)}
                      >
                        <img
                          src={coverImage}
                          alt=""
                          className="absolute top-0 left-0 w-full h-full z-[-1] rounded-md object-cover"
                          aria-hidden
                        />
                        <img
                          src={item.previewImage}
                          alt={item.name}
                          className="w-full h-full object-contain z-[3]"
                        />
                      </div>

                      {/* Name + price area */}
                      <div className="flex-1 min-w-0 ml-5">
                        <h3
                          className="text-white text-[16px] font-semibold gilroy-font cursor-pointer hover:text-gray-300"
                          onClick={() => onItemClick(item)}
                        >
                          {item.name}
                        </h3>

                        <div className="flex items-center space-x-2 mt-1">
                          <div className="relative bg-gradient-to-b from-[#47FF94]/40 to-[#285743]/70 px-3 py-1 rounded-sm border-[1px] border-[#47FF94]/20 border-b-0">
                            <p className="text-[#47FF94] text-[14px]">
                              {renderPrice(item.price)}
                            </p>
                          </div>

                          {showQuantity && item.quantity && (
                            <span className="text-xs text-gray-400">
                              x{item.quantity}
                            </span>
                          )}

                          {showSubtotal &&
                            item.quantity &&
                            item.quantity > 1 && (
                              <span className="text-xs text-gray-300">
                                = {renderPrice(item.price * item.quantity)}
                              </span>
                            )}
                        </div>
                      </div>

                      {/* Remove button */}
                      <button
                        onClick={() => {
                          playEffect(trashSound);
                          removeFromCart(item.productId);
                        }}
                        className="h-[60px] rounded transition-colors flex-shrink-0 flex items-center justify-center hover:opacity-90 active:scale-95 relative min-w-[45px] group text-gray-400 hover:text-red-600"
                        aria-label={`${removeButtonAriaLabel} ${item.name}`}
                      >
                        <img
                          src={trashBg}
                          alt=""
                          className="absolute top-0 left-0 w-full h-full object-contain"
                          aria-hidden
                        />
                        {Remove ? (
                          <Remove className="text-2xl" />
                        ) : (
                          <span className="w-4 h-4">🗑</span>
                        )}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* ----- Footer: total + actions ----- */}
            <div className="space-y-3">
              <TotalPrice total={total} currency={currency} />

              {items.length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={onPurchaseByCard}
                    title={cardButtonText}
                    className="relative rounded-md overflow-hidden min-h-[30px] flex items-center justify-center text-center text-white w-full py-2 mt-2 hover:opacity-80 active:scale-95 transition"
                  >
                    <img
                      src={cartBtnBg}
                      alt=""
                      className="absolute top-0 left-0 right-0 h-full"
                      aria-hidden
                    />
                    {cardButtonText}
                  </button>

                  <button
                    onClick={onPurchaseByCash}
                    title={cashButtonText}
                    className="relative rounded-md overflow-hidden min-h-[30px] flex items-center justify-center text-center text-white w-full py-2 mt-2 hover:opacity-80 active:scale-95 transition"
                  >
                    <img
                      src={cartBtnBg}
                      alt=""
                      className="absolute top-0 left-0 right-0 h-full"
                      aria-hidden
                    />
                    {cashButtonText}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ShoppingCart;
